import { AxiosResponseHeaders } from "axios";
import { IApiClient } from "../api-client";
import { EnvType } from "../services";
import { AddMarkerByObjectIdRequest } from "../Types/add-marker-by-object-id-request";
import { AddMarkerByObjectIdResponse } from "../Types/add-marker-by-object-id-response";
import { AddOrUpdateMarkersByObjectIdRequest } from "../Types/add-or-update-markers-by-object-id-request";
import { ChangeStatusRequest } from "../Types/change-status-request";
import { CheckUpdateMetadataFieldsRequest } from "../Types/check-update-metadata-fields-request";
import { CheckUpdateMetadataFieldsResponse } from "../Types/check-update-metadata-fields-response";
import { CollectionRequest } from "../Types/collection-request";
import { CollectionResponse } from "../Types/collection-response";
import { CopyObjectRequest } from "../Types/copy-object-request";
import { CreateClipRequest } from "../Types/create-clip-request";
import { CreateClipResponse } from "../Types/create-clip-response";
import { CreateRequest } from "../Types/create-request";
import { CreateResponse } from "../Types/create-response";
import { CreateStandaloneRequest } from "../Types/create-standalone-request";
import { CreateStandaloneResponse } from "../Types/create-standalone-response";
import { CollectionType } from "../Types/db-collection-type";
import { DbMarkerCategory } from "../Types/db-marker-category";
import { DeleteSpecificPermissionRequest } from "../Types/delete-specific-permission-request";
import { DuplicateMarkerFromMarkerIdRequest } from "../Types/duplicate-marker-from-marker-id-request";
import { GenerateCompilationRequest } from "../Types/generate-compilation-request";
import { GenerateCompilationResponse } from "../Types/generate-compilation-response";
import { GenericResponseStd } from "../Types/generic-response-std";
import { GetObjectQuotasResponse } from "../Types/get-asset-object-quotas-response";
import { GetBundleMakerComputeMetadataRequest } from "../Types/get-bundle-maker-compute-metadata-request";
import { GetBundleMakerMetadataResponse } from "../Types/get-bundle-maker-metadata-response";
import { GetChildrenResponse } from "../Types/get-children-response";
import { GetChildrenSizeGroupedByObjectTypeResponse } from "../Types/get-children-size-grouped-by-object-type-response";
import { GetGraphResponse } from "../Types/get-graph-response";
import { GetLayerMetadataListResponse } from "../Types/get-layer-metadata-list-response";
import { GetMarkerEnumsResponse } from "../Types/get-marker-enums-response";
import { GetMarkersByObjectIdResponse } from "../Types/get-markers-by-object-id-response";
import { GetMetadataFieldsByObjectIdResponse } from "../Types/get-metadata-fields-by-object-id-response";
import { GetMetadataFieldsFlatResponse } from "../Types/get-metadata-fields-flat-response";
import { GetMetadataFieldsResponse } from "../Types/get-metadata-fields-response";
import { GetPermissionsByObjectIdResponse } from "../Types/get-permissions-by-object-id-response";
import { GetPlatformLanguageCodes } from "../Types/get-platform-language-codes";
import { GetStatusTransitionsResponse } from "../Types/get-status-transitions-response";
import { GroupAssetsRequest } from "../Types/group-assets-request";
import { ImportMarkersRequest } from "../Types/import-markers-request";
import { Languages } from "../Types/languages";
import { MetadataExportResponse } from "../Types/metadata-export-response";
import { MoveObjectRequest } from "../Types/move-object-request";
import { ObjectLayer } from "../Types/object-layer";
import { RenameRequest } from "../Types/rename-request";
import { SearchAdvancedResponse } from "../Types/search-advanced-response";
import { SearchResponse } from "../Types/search-response";
import { SetConfidentialityInfosRequest } from "../Types/set-confidentiality-infos-request";
import { SetFollowersRequest } from "../Types/set-followers-request";
import { SetTagsPropertyRequest } from "../Types/set-tags-property-request";
import { SingleCollectionResponse } from "../Types/single-collection-response";
import { SwitchClassRequest } from "../Types/switch-class-request";
import { UpdateCompilationRequest } from "../Types/update-compilation-request ";
import { UpdateMetadataFieldsRequest } from "../Types/update-metadata-fields-request";
import { UpdateMetadataResponse } from "../Types/update-metadata-response";
import { SetDefaultPermissionsRequest } from "../Types/set-default-permissions-request";
import { SetSpecificPermissionsRequest } from "../Types/set-specific-permissions-request";
import { ReplaceResourceRequest } from "../Types/replace-resource-request";
import { UpdateObjectsMetadataFieldsRequest } from "../Types/update-objects-metadata-fields-request";
import { ObjectType } from "../Types/object-type";
import { UpdateClipByIdRequest } from "../Types/update-clip-by-id-request";

interface IAssetApiClient {
  GetMetadataFields(): Promise<GetMetadataFieldsResponse>;
  GetMetadataFieldsFlat(): Promise<GetMetadataFieldsFlatResponse>;
  GetLayerMetadataList(layer: ObjectLayer): Promise<GetLayerMetadataListResponse>;
  GetMetadataLanguageField(): Promise<GetPlatformLanguageCodes>;
  GetMetadataFieldsByObjectId(objectId: number): Promise<GetMetadataFieldsByObjectIdResponse>;
  UpdateMetadataFields(request: UpdateMetadataFieldsRequest): Promise<UpdateMetadataResponse>;
  DeleteMetadataFields(request: UpdateMetadataFieldsRequest): Promise<UpdateMetadataResponse>;
  RemoveCulturesFromMetadata(objectId: number, cultureIds: number[]): Promise<UpdateMetadataResponse>;
  ExportObjectMetadata(objectId: number, includeChildren: boolean): Promise<MetadataExportResponse>;
  GetGraphByObjectId(objectId: number): Promise<GetGraphResponse>;
  GetGraphExtendedByObjectId(objectId: number): Promise<GetGraphResponse>;
  GetGraphExtendedLightByObjectId(objectId: number): Promise<GetGraphResponse>;
  GetGraphExtendedLightNoVisitByObjectId(objectId: number): Promise<GetGraphResponse>;
  GetTitleInfosByObjectId(objectId: number): Promise<GetGraphResponse>;
  GetTitleInfosByObjectIds(objectIds: number[]): Promise<SearchResponse>;
  GetObjectById(objectId: number, aggregateAll: boolean, childRecursiveDepth: number | null): Promise<GetGraphResponse>;
  GetObjectLightById(objectId: number, childRecursiveDepth: number): Promise<GetGraphResponse>;
  GetChildrenByObjectId(parentObjectId: number, excludeIds: number[], size: number): Promise<GetChildrenResponse>;
  DownloadTitleAsExcelFile(titleId: number): Promise<GenericResponseStd>;
  ListLastObjects(layer: ObjectLayer, maxItems: number): Promise<SearchResponse>;
  ListHeadObjects(): Promise<SearchResponse>;
  GetModifiedObjectsByDateRange(startDate: string, endDate: string): Promise<SearchResponse>;
  CreateByTemplate(objects: CreateRequest[]): Promise<CreateResponse>;
  CreateStandalone(request: CreateStandaloneRequest): Promise<CreateStandaloneResponse>;
  CreateClip(request: CreateClipRequest): Promise<CreateClipResponse>;
  GroupAssets(request: GroupAssetsRequest): Promise<GenericResponseStd>;
  Rename(request: RenameRequest): Promise<GenericResponseStd>;
  DeleteObjectById(objectId: number): Promise<GenericResponseStd>;
  ChangeStatus(request: ChangeStatusRequest): Promise<GenericResponseStd>;
  ChangeFollower(objectId: number, userId: number): Promise<GenericResponseStd>;
  DownloadCreatedObjectsByDateRangeAsCSV(
    startDateAsString: string,
    endDateAsString: string
  ): Promise<GenericResponseStd>;
  CopyObjectAsync(request: CopyObjectRequest): Promise<GenericResponseStd>;
  MoveObjectAsync(request: MoveObjectRequest): Promise<GenericResponseStd>;
  SaveObjects(objectIds: number[]): Promise<GenericResponseStd>;
  SwitchObjectClass(request: SwitchClassRequest): Promise<GenericResponseStd>;
  SetUploader(objectId: number): Promise<GenericResponseStd>;
  SetFollowers(request: SetFollowersRequest): Promise<GenericResponseStd>;
  DownloadAssetPdfExport(
    objectId: number,
    language: Languages,
    markerCategory?: DbMarkerCategory
  ): Promise<[string, AxiosResponseHeaders]>;
  SetConfidentialityInfos(request: SetConfidentialityInfosRequest): Promise<GenericResponseStd>;
  SetTagsProperty(request: SetTagsPropertyRequest): Promise<GenericResponseStd>;
  WhoIam(objectId: number): Promise<GenericResponseStd>;
  GetMetrics(objectId: number): Promise<GetChildrenSizeGroupedByObjectTypeResponse>;
  GetClipsByObjectId(objectId: number): Promise<SearchAdvancedResponse>;
  GetClipsByObjectIdWithEpisodeNumber(objectId: number, episodeNb: number): Promise<SearchAdvancedResponse>;
  GetMarkerEnums(): Promise<GetMarkerEnumsResponse>;
  GetMarkersByObjectId(objectId: number): Promise<GetMarkersByObjectIdResponse>;
  ImportMarkers(request: ImportMarkersRequest): Promise<GenericResponseStd>;
  ImportMarkersWithStagging(request: ImportMarkersRequest): Promise<GenericResponseStd>;
  AddMarkerOnObjectId(request: AddMarkerByObjectIdRequest): Promise<AddMarkerByObjectIdResponse>;
  UpdateMarkerOnObjectId(request: AddMarkerByObjectIdRequest): Promise<AddMarkerByObjectIdResponse>;
  AddOrUpdateMarkersByObjectId(request: AddOrUpdateMarkersByObjectIdRequest): Promise<GenericResponseStd>;
  DuplicateMarkerFromMarkerId(request: DuplicateMarkerFromMarkerIdRequest): Promise<GenericResponseStd>;
  DeleteMarkersByObjectId(objectId: number): Promise<GenericResponseStd>;
  DeleteMarkerByMarkerId(objectId: number): Promise<GenericResponseStd>;
  GetBundleMakerComputeBaseMetadata(titleId: number): Promise<GetBundleMakerMetadataResponse>;
  GetBundleMakerComputeCulturedMetadata(
    request: GetBundleMakerComputeMetadataRequest
  ): Promise<GetBundleMakerMetadataResponse>;
  SetDefaultPermissions(request: SetDefaultPermissionsRequest): Promise<GenericResponseStd>;
  SetSpecificPermissions(request: SetSpecificPermissionsRequest): Promise<GenericResponseStd>;
  DeleteSpecificPermissions(request: DeleteSpecificPermissionRequest): Promise<GenericResponseStd>;
  GetPermissionsByObjectId(objectId: number): Promise<GetPermissionsByObjectIdResponse>;
  PutToTrash(objectId: number): Promise<GenericResponseStd>;
  ClearTrash(objectIds: number[]): Promise<GenericResponseStd>;
  UnTrash(objectId: number): Promise<GenericResponseStd>;
  GetTrash(): Promise<SearchResponse>;
  ListCollection(): Promise<CollectionResponse>;
  ListCollectionByType(type: CollectionType): Promise<CollectionResponse>;
  CreateCollection(request: CollectionRequest): Promise<GenericResponseStd>;
  UpdateCollection(request: CollectionRequest): Promise<GenericResponseStd>;
  AddObjectIdsToCollection(collectionId: number, objectIds: number[]): Promise<GenericResponseStd>;
  DeleteCollection(objectId: number): Promise<GenericResponseStd>;
  GetCollection(Id: number): Promise<SingleCollectionResponse>;
  DeleteFromCollection(collectionId: number, objectIds: number[]): Promise<GenericResponseStd>;
  SearchCollectionByObjectIds(objectIds: number[]): Promise<CollectionResponse>;
  ToggleMetadataViewFilterById(id: number, folderId: number): Promise<GenericResponseStd>;
  GenerateCompilation(request: GenerateCompilationRequest): Promise<GenerateCompilationResponse>;
  GetStatusTransitions(objectId: number): Promise<GetStatusTransitionsResponse>;
  CheckUpdateMetadataFields(request: CheckUpdateMetadataFieldsRequest): Promise<CheckUpdateMetadataFieldsResponse>;
  ReplaceLinkedResource(request: ReplaceResourceRequest): Promise<GenericResponseStd>;
}

export default class AssetApiClient implements IAssetApiClient {
  readonly servicePort = 9503;
  readonly serviceBaseUrl = "asset-service/api";

  assetApiClient: IApiClient;

  constructor(assetApiClient: IApiClient, envType: EnvType) {
    this.assetApiClient = assetApiClient;
    if (envType === "local") this.assetApiClient.client.defaults.baseURL += `${this.servicePort}/`;
  }

  async CheckUpdateMetadataFields(
    request: CheckUpdateMetadataFieldsRequest
  ): Promise<CheckUpdateMetadataFieldsResponse> {
    return this.assetApiClient.post(`${this.serviceBaseUrl}/metadata/check-update`, request);
  }
  async GetMetadataFields(): Promise<GetMetadataFieldsResponse> {
    return await this.assetApiClient.get(`${this.serviceBaseUrl}/metadata/fields`);
  }
  async GetMetadataFieldsFlat(): Promise<GetMetadataFieldsFlatResponse> {
    return await this.assetApiClient.get(`${this.serviceBaseUrl}/metadata/fields/flat`);
  }
  async GetLayerMetadataList(layer: ObjectLayer): Promise<GetLayerMetadataListResponse> {
    return await this.assetApiClient.get(`${this.serviceBaseUrl}/object/metadata/${layer}`);
  }
  async GetMetadataLanguageField(): Promise<GetPlatformLanguageCodes> {
    return await this.assetApiClient.get(`${this.serviceBaseUrl}/metadata/languages`);
  }
  async GetMetadataFieldsByObjectId(objectId: number): Promise<GetMetadataFieldsByObjectIdResponse> {
    return await this.assetApiClient.get(`${this.serviceBaseUrl}/metadata/fields/${objectId}`);
  }
  async GetMetadataFieldsByObjectIds(objectIds: number[]): Promise<GetMetadataFieldsByObjectIdResponse> {
    return await this.assetApiClient.post(`${this.serviceBaseUrl}/metadata/fields/intersection`, objectIds);
  }
  async UpdateMetadataFields(request: UpdateMetadataFieldsRequest): Promise<UpdateMetadataResponse> {
    return await this.assetApiClient.put(`${this.serviceBaseUrl}/metadata/update`, request);
  }
  async UpdateObjectsMetadataFields(request: UpdateObjectsMetadataFieldsRequest): Promise<UpdateMetadataResponse> {
    return await this.assetApiClient.put(`${this.serviceBaseUrl}/metadata/update/multi`, request);
  }
  async DeleteMetadataFields(request: UpdateMetadataFieldsRequest): Promise<UpdateMetadataResponse> {
    return await this.assetApiClient.delete(`${this.serviceBaseUrl}/metadata/delete`, request);
  }
  async RemoveCulturesFromMetadata(objectId: number, cultureIds: number[]): Promise<UpdateMetadataResponse> {
    return await this.assetApiClient.post(`${this.serviceBaseUrl}/metadata/remove-cultures`, {
      objectId: objectId,
      cultureIds: cultureIds,
    });
  }
  async ExportObjectMetadata(objectId: number, includeChildren: boolean): Promise<MetadataExportResponse> {
    return await this.assetApiClient.get(`${this.serviceBaseUrl}/metadata/export/${objectId}/${includeChildren}`);
  }
  async GetGraphByObjectId(objectId: number): Promise<GetGraphResponse> {
    return await this.assetApiClient.get(`${this.serviceBaseUrl}/graph/${objectId}`);
  }
  async GetGraphExtendedByObjectId(objectId: number): Promise<GetGraphResponse> {
    return await this.assetApiClient.get(`${this.serviceBaseUrl}/graph/extended/${objectId}`);
  }
  async GetGraphExtendedLightByObjectId(objectId: number): Promise<GetGraphResponse> {
    return await this.assetApiClient.get(`${this.serviceBaseUrl}/graph/extended-light/${objectId}`);
  }
  async GetGraphExtendedLightNoVisitByObjectId(objectId: number): Promise<GetGraphResponse> {
    return await this.assetApiClient.get(`${this.serviceBaseUrl}/graph/extended-light-novisit/${objectId}`);
  }
  async GetTitleInfosByObjectId(objectId: number): Promise<GetGraphResponse> {
    return await this.assetApiClient.get(`${this.serviceBaseUrl}/graph/title-infos/${objectId}`);
  }
  async GetTitleInfosByObjectIds(objectIds: number[]): Promise<SearchResponse> {
    return await this.assetApiClient.post(`${this.serviceBaseUrl}/graph/title-infos`, objectIds);
  }
  async GetObjectById(objectId: number): Promise<GetGraphResponse> {
    return await this.assetApiClient.get(`${this.serviceBaseUrl}/graph/object/${objectId}`);
  }
  async GetObjectLightById(objectId: number, childRecursiveDepth: number): Promise<GetGraphResponse> {
    return await this.assetApiClient.get(
      `${this.serviceBaseUrl}/graph/object-light/${objectId}/${childRecursiveDepth}`
    );
  }
  async GetChildrenByObjectId(
    parentObjectId: number,
    excludeIds: number[],
    size: number,
    page?: number,
    onlyObjectTypes?: ObjectType[]
  ): Promise<GetChildrenResponse> {
    return await this.assetApiClient.post(`${this.serviceBaseUrl}/graph/children`, {
      parentObjectId,
      size,
      page,
      excludeIds,
      onlyObjectTypes,
    });
  }
  async DownloadTitleAsExcelFile(titleId: number): Promise<GenericResponseStd> {
    return await this.assetApiClient.post(`${this.serviceBaseUrl}/graph/${titleId}/excel`);
  }
  async ListLastObjects(layer: ObjectLayer, maxItems: number): Promise<SearchResponse> {
    return await this.assetApiClient.get(`${this.serviceBaseUrl}/object/latests/${layer}/${maxItems}`);
  }
  async ListHeadObjects(): Promise<SearchResponse> {
    return await this.assetApiClient.get(`${this.serviceBaseUrl}/object/heads`);
  }
  async GetModifiedObjectsByDateRange(startDate: string, endDate: string): Promise<SearchResponse> {
    return await this.assetApiClient.get(`${this.serviceBaseUrl}/object/modified/${startDate}/${endDate}`);
  }
  async CreateByTemplate(objects: CreateRequest[]): Promise<CreateResponse> {
    return await this.assetApiClient.post(`${this.serviceBaseUrl}/object/template`, objects);
  }
  async CreateStandalone(request: CreateStandaloneRequest): Promise<CreateStandaloneResponse> {
    return await this.assetApiClient.post(`${this.serviceBaseUrl}/object/standalone`, request);
  }
  async CreateClip(request: CreateClipRequest): Promise<CreateClipResponse> {
    return await this.assetApiClient.post(`${this.serviceBaseUrl}/object/clip`, request);
  }
  async UpdateClipById(request: UpdateClipByIdRequest): Promise<CreateClipResponse> {
    return await this.assetApiClient.put(`${this.serviceBaseUrl}/object/clip`, request);
  }
  async GroupAssets(request: GroupAssetsRequest): Promise<GenericResponseStd> {
    return await this.assetApiClient.post(`${this.serviceBaseUrl}/object/group`, request);
  }
  async Rename(request: RenameRequest): Promise<GenericResponseStd> {
    return await this.assetApiClient.put(`${this.serviceBaseUrl}/object/name`, request);
  }
  async DeleteObjectById(objectId: number): Promise<GenericResponseStd> {
    return await this.assetApiClient.delete(`${this.serviceBaseUrl}/object/${objectId}`);
  }
  async ChangeStatus(request: ChangeStatusRequest): Promise<GenericResponseStd> {
    return await this.assetApiClient.put(`${this.serviceBaseUrl}/object/status`, request);
  }
  async ChangeFollower(objectId: number, userId: number): Promise<GenericResponseStd> {
    return await this.assetApiClient.put(`${this.serviceBaseUrl}/object/${objectId}/follower/${userId}`);
  }
  async DownloadCreatedObjectsByDateRangeAsCSV(
    startDateAsString: string,
    endDateAsString: string
  ): Promise<GenericResponseStd> {
    return await this.assetApiClient.get(
      `${this.serviceBaseUrl}/object/download/${startDateAsString}/${endDateAsString}/csv`
    );
  }
  async CopyObjectAsync(request: CopyObjectRequest): Promise<GenericResponseStd> {
    return await this.assetApiClient.post(`${this.serviceBaseUrl}/object/copy`, request);
  }
  async MoveObjectAsync(request: MoveObjectRequest): Promise<GenericResponseStd> {
    return await this.assetApiClient.put(`${this.serviceBaseUrl}/object/move`, request);
  }
  async SaveObjects(objectIds: number[]): Promise<GenericResponseStd> {
    return await this.assetApiClient.put(`${this.serviceBaseUrl}/object/save`, objectIds);
  }
  async SaveObjectsByType(objectType: ObjectType): Promise<GenericResponseStd> {
    return await this.assetApiClient.put(`${this.serviceBaseUrl}/objects/save/${objectType}`);
  }
  async SwitchObjectClass(request: SwitchClassRequest): Promise<GenericResponseStd> {
    return await this.assetApiClient.put(`${this.serviceBaseUrl}/object/switch-class`, request);
  }
  async SetUploader(objectId: number): Promise<GenericResponseStd> {
    return await this.assetApiClient.put(`${this.serviceBaseUrl}/object/uploaded/${objectId}`);
  }
  async SetFollowers(request: SetFollowersRequest): Promise<GenericResponseStd> {
    return await this.assetApiClient.post(`${this.serviceBaseUrl}/object/followers`, request);
  }
  async DownloadAssetPdfExport(
    objectId: number,
    language: Languages,
    markerCategory?: DbMarkerCategory
  ): Promise<[string, AxiosResponseHeaders]> {
    return await this.assetApiClient.getWithHeaders(
      `${this.serviceBaseUrl}/object/${objectId}/${language}/pdf` + (markerCategory ? "/" + markerCategory : ""),
      "arraybuffer"
    );
  }
  async SetConfidentialityInfos(request: SetConfidentialityInfosRequest): Promise<GenericResponseStd> {
    return await this.assetApiClient.put(`${this.serviceBaseUrl}/object/confidentiality`, request);
  }
  async SetTagsProperty(request: SetTagsPropertyRequest): Promise<GenericResponseStd> {
    return await this.assetApiClient.put(`${this.serviceBaseUrl}/object/tag`, request);
  }
  async WhoIam(objectId: number): Promise<GenericResponseStd> {
    return await this.assetApiClient.get(`${this.serviceBaseUrl}/object/whoiam/${objectId}`);
  }
  async GetMetrics(objectId: number): Promise<GetChildrenSizeGroupedByObjectTypeResponse> {
    return await this.assetApiClient.get(`${this.serviceBaseUrl}/object/metrics/${objectId}`);
  }
  async GetQuotas(objectId: number): Promise<GetObjectQuotasResponse> {
    return await this.assetApiClient.get(`${this.serviceBaseUrl}/object/quotas/${objectId}`);
  }
  async GetClipsByObjectId(objectId: number): Promise<SearchAdvancedResponse> {
    return await this.assetApiClient.get(`${this.serviceBaseUrl}/clips/${objectId}`);
  }
  async GetClipsByObjectIdWithEpisodeNumber(objectId: number, episodeNb: number): Promise<SearchAdvancedResponse> {
    return await this.assetApiClient.get(`${this.serviceBaseUrl}/clips/${objectId}/${episodeNb}`);
  }
  async GetMarkerEnums(): Promise<GetMarkerEnumsResponse> {
    return await this.assetApiClient.get(`${this.serviceBaseUrl}/marker/enums`);
  }
  async GetMarkersByObjectId(objectId: number): Promise<GetMarkersByObjectIdResponse> {
    return await this.assetApiClient.get(`${this.serviceBaseUrl}/marker/${objectId}`);
  }
  async ImportMarkers(request: ImportMarkersRequest): Promise<GenericResponseStd> {
    return await this.assetApiClient.post(`${this.serviceBaseUrl}/marker/qcml`, request);
  }
  async ImportMarkersWithStagging(request: ImportMarkersRequest): Promise<GenericResponseStd> {
    return await this.assetApiClient.post(`${this.serviceBaseUrl}/marker/qcml-stagging`, request);
  }
  async AddMarkerOnObjectId(request: AddMarkerByObjectIdRequest): Promise<AddMarkerByObjectIdResponse> {
    return await this.assetApiClient.post(`${this.serviceBaseUrl}/marker/addone`, request);
  }
  async UpdateMarkerOnObjectId(request: AddMarkerByObjectIdRequest): Promise<AddMarkerByObjectIdResponse> {
    return await this.assetApiClient.put(`${this.serviceBaseUrl}/marker/updateone`, request);
  }
  async AddOrUpdateMarkersByObjectId(request: AddOrUpdateMarkersByObjectIdRequest): Promise<GenericResponseStd> {
    return await this.assetApiClient.post(`${this.serviceBaseUrl}/marker`, request);
  }
  async DuplicateMarkerFromMarkerId(request: DuplicateMarkerFromMarkerIdRequest): Promise<GenericResponseStd> {
    return await this.assetApiClient.post(`${this.serviceBaseUrl}/marker/duplicate`, request);
  }
  async DeleteMarkersByObjectId(objectId: number): Promise<GenericResponseStd> {
    return await this.assetApiClient.delete(`${this.serviceBaseUrl}/marker/${objectId}`);
  }
  async DeleteMarkerByMarkerId(objectId: number): Promise<GenericResponseStd> {
    return await this.assetApiClient.delete(`${this.serviceBaseUrl}/marker/alone/${objectId}`);
  }
  async GetBundleMakerComputeBaseMetadata(titleId: number): Promise<GetBundleMakerMetadataResponse> {
    return await this.assetApiClient.get(`${this.serviceBaseUrl}/bundlemaker/metadata/${titleId}`);
  }
  async GetBundleMakerComputeCulturedMetadata(
    request: GetBundleMakerComputeMetadataRequest
  ): Promise<GetBundleMakerMetadataResponse> {
    return await this.assetApiClient.post(`${this.serviceBaseUrl}/bundlemaker/metadata`, request);
  }
  async SetDefaultPermissions(request: SetDefaultPermissionsRequest): Promise<GenericResponseStd> {
    return await this.assetApiClient.post(`${this.serviceBaseUrl}/object/permissions/default`, request);
  }
  async SetSpecificPermissions(request: SetSpecificPermissionsRequest): Promise<GenericResponseStd> {
    return await this.assetApiClient.post(`${this.serviceBaseUrl}/object/permissions/specific`, request);
  }
  async DeleteSpecificPermissions(request: DeleteSpecificPermissionRequest): Promise<GenericResponseStd> {
    return await this.assetApiClient.delete(`${this.serviceBaseUrl}/object/permissions/specific`, request);
  }
  async GetPermissionsByObjectId(objectId: number): Promise<GetPermissionsByObjectIdResponse> {
    return await this.assetApiClient.get(`${this.serviceBaseUrl}/object/${objectId}/permissions`);
  }
  async PutToTrash(objectId: number): Promise<GenericResponseStd> {
    return await this.assetApiClient.put(`${this.serviceBaseUrl}/object/trash/${objectId}`);
  }
  async ClearTrash(objectIds: number[]): Promise<GenericResponseStd> {
    return await this.assetApiClient.delete(`${this.serviceBaseUrl}/object/trash`, objectIds);
  }
  async UnTrash(objectId: number): Promise<GenericResponseStd> {
    return await this.assetApiClient.put(`${this.serviceBaseUrl}/object/untrash/${objectId}`);
  }
  async GetTrash(): Promise<SearchResponse> {
    return await this.assetApiClient.get(`${this.serviceBaseUrl}/object/trash`);
  }
  async ListCollection(): Promise<CollectionResponse> {
    return await this.assetApiClient.get(`${this.serviceBaseUrl}/collections`);
  }
  async ListCollectionByType(type: CollectionType): Promise<CollectionResponse> {
    return await this.assetApiClient.get(`${this.serviceBaseUrl}/collections/type/${type}`);
  }
  async CreateCollection(request: CollectionRequest): Promise<GenericResponseStd> {
    return await this.assetApiClient.post(`${this.serviceBaseUrl}/collections`, request);
  }
  async UpdateCollection(request: CollectionRequest): Promise<GenericResponseStd> {
    return await this.assetApiClient.put(`${this.serviceBaseUrl}/collections`, request);
  }
  async AddObjectIdsToCollection(collectionId: number, objectIds: number[]): Promise<GenericResponseStd> {
    return await this.assetApiClient.put(`${this.serviceBaseUrl}/collections/update`, {
      objectIds: objectIds,
      collectionId: collectionId,
    });
  }
  async DeleteCollection(Id: number): Promise<GenericResponseStd> {
    return await this.assetApiClient.delete(`${this.serviceBaseUrl}/collections/${Id}`);
  }
  async GetCollection(Id: number): Promise<SingleCollectionResponse> {
    return await this.assetApiClient.get(`${this.serviceBaseUrl}/collections/${Id}`);
  }
  async DeleteFromCollection(collectionId: number, objectIds: number[]): Promise<GenericResponseStd> {
    return await this.assetApiClient.delete(`${this.serviceBaseUrl}/collections/update`, {
      objectIds: objectIds,
      collectionId: collectionId,
    });
  }
  async SearchCollectionByObjectIds(objectIds: number[]): Promise<CollectionResponse> {
    return await this.assetApiClient.post(`${this.serviceBaseUrl}/collections/search`, objectIds);
  }
  async ToggleMetadataViewFilterById(objectId: number, viewId: number): Promise<GenericResponseStd> {
    return await this.assetApiClient.put(`${this.serviceBaseUrl}/object/${objectId}/view/${viewId}`);
  }
  async GenerateCompilation(request: GenerateCompilationRequest): Promise<GenerateCompilationResponse> {
    return await this.assetApiClient.post(`${this.serviceBaseUrl}/timetool/compilation-sequence/generate`, request);
  }
  async UpdateCompilation(request: UpdateCompilationRequest): Promise<GenerateCompilationResponse> {
    return await this.assetApiClient.post(`${this.serviceBaseUrl}/timetool/compilation-sequence/update`, request);
  }
  async GetStatusTransitions(objectId: number): Promise<GetStatusTransitionsResponse> {
    return await this.assetApiClient.get(`${this.serviceBaseUrl}/object/${objectId}/status-transitions`);
  }
  async ReplaceLinkedResource(request: ReplaceResourceRequest): Promise<GenericResponseStd> {
    return await this.assetApiClient.post(`${this.serviceBaseUrl}/linked-resources`, request);
  }
  //------Utils------
  GetApiUrl(): string {
    return this.assetApiClient.client.defaults.baseURL + this.serviceBaseUrl;
  }
}
